import {useAuth} from '@/shared/contexts/AuthContext.tsx';
import {History, LogOut, User, Wallet} from 'lucide-react';
import {useCallback, useMemo, useState} from 'react';
import {useNavigate} from 'react-router';

type HeaderProps = {
    title: string;
};

const ROLE_LABELS: Record<string, string> = {
    owner: 'Proprietário',
    receptionist: 'Recepção',
    employee: 'Profissional',
};

const getRoleLabel = (role: string): string => {
    return ROLE_LABELS[role] ?? role;
};

export const Header = ({title}: HeaderProps) => {
    const {employee, signOut, role} = useAuth();
    const navigate = useNavigate();
    const [showUserMenu, setShowUserMenu] = useState(false);

    const handleSignOut = useCallback(async () => {
        setShowUserMenu(false);
        await signOut();
    }, [signOut]);

    const handleNavigateToSaldo = useCallback(() => {
        setShowUserMenu(false);
        navigate('/admin/saldo');
    }, [navigate]);

    const handleNavigateToHistory = useCallback(() => {
        setShowUserMenu(false);
        // Navigate to audit trail based on current role
        if (role === 'owner') {
            navigate('/admin/historico');
        } else if (role === 'receptionist') {
            navigate('/recepcionista/historico');
        } else if (role === 'employee') {
            navigate('/profissional/historico');
        }
    }, [navigate, role]);

    const handleToggleMenu = useCallback(() => {
        setShowUserMenu((prev) => !prev);
    }, []);

    const handleCloseMenu = useCallback(() => {
        setShowUserMenu(false);
    }, []);

    // For employees, show their initial and name; for others, show "C" and the tab title
    const isEmployee = role === 'employee';
    const employeeName = employee?.name || '';

    const brandLogo = useMemo(() => {
        if (isEmployee) {
            return employeeName.charAt(0).toUpperCase() || 'E';
        }
        return 'C';
    }, [isEmployee, employeeName]);

    const headerTitle = useMemo(() => {
        if (isEmployee) {
            return employeeName || title;
        }
        return title;
    }, [isEmployee, employeeName, title]);

    const userInitial = useMemo(() => {
        return employeeName.charAt(0).toUpperCase() || 'U';
    }, [employeeName]);

    const roleLabel = useMemo(() => {
        return getRoleLabel(employee?.role || '');
    }, [employee?.role]);

    return (
        <header className="app-header">
            <div className="header-content">
                <div className="header-left">
                    <div className="header-brand">
                        <div className="brand-logo">{brandLogo}</div>
                        <div className="brand-text">
                            <h1>{headerTitle}</h1>
                            <span className="brand-subtitle">Conquest Cabeleireiro</span>
                        </div>
                    </div>
                </div>

                <div className="header-right">
                    {/* User Menu */}
                    <div className="user-menu-wrapper">
                        <button className="header-btn user-btn" onClick={handleToggleMenu}>
                            <User size={20} />
                        </button>

                        {showUserMenu && (
                            <>
                                <div className="user-menu-overlay" onClick={handleCloseMenu} />
                                <div className="user-menu">
                                    <div className="user-menu-header">
                                        <div className="user-avatar">{userInitial}</div>
                                        <div className="user-info">
                                            <span className="user-name">{employeeName}</span>
                                            <span className="user-role">{roleLabel}</span>
                                        </div>
                                    </div>
                                    <div className="user-menu-divider" />
                                    {/* Saldo - Owner Only */}
                                    {role === 'owner' && (
                                        <button
                                            className="user-menu-item"
                                            onClick={handleNavigateToSaldo}
                                        >
                                            <Wallet size={18} />
                                            Saldo
                                        </button>
                                    )}
                                    {/* Histórico - Audit Trail */}
                                    <button
                                        className="user-menu-item"
                                        onClick={handleNavigateToHistory}
                                    >
                                        <History size={18} />
                                        Histórico
                                    </button>
                                    <button
                                        className="user-menu-item logout"
                                        onClick={handleSignOut}
                                    >
                                        <LogOut size={18} />
                                        Sair
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </header>
    );
};
